// Thin clients. The worker screens call mdo-api /v1/me/* with the worker's own Supabase session token.
// The owner console calls GM's ops-mdo proxy with the operator's session token. Nothing here touches a database.

export type TokenGetter = () => Promise<string | null>;

async function call<T>(base: string, getToken: TokenGetter, method: string, path: string, body?: unknown): Promise<T> {
  const token = await getToken();
  const r = await fetch(`${base.replace(/\/$/, "")}${path}`, {
    method,
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw Object.assign(new Error((data as { message?: string; error?: string }).message ?? (data as { error?: string }).error ?? String(r.status)), { status: r.status, data });
  return data as T;
}

// ---------------------------------------------------------------- worker
export type OficioCard = {
  oficio_key: string; name_es: string; name_en: string; color: string | null;
  titulo: "aprendiz" | "oficial" | "maestro" | null; next_titulo: string | null; approved_obras: number; obras_needed: number;
  maestro_ready: boolean; obra_maestra_marked: boolean; visibility: "solo_yo" | "compartido" | "publico" | null;
  carta_number: string | null; visto_bueno_requested: boolean;
};
export type RecentJob = { obra_id: string; oficio_key: string; work_date: string | null; state: "counted" | "tip" | "waiting" | "reviewing";
  tip_es: string | null; tip_en: string | null; can_appeal: boolean };
export type Summary = { oficios: OficioCard[]; portfolio: { counted: number; reviewing: number }; recent_jobs: RecentJob[];
  pending_tags: { obra_id: string; oficio_key: string; work_date: string | null; lead_name: string }[] };

export function mdoClient(apiUrl: string, getToken: TokenGetter) {
  const c = <T>(m: string, p: string, b?: unknown) => call<T>(apiUrl, getToken, m, p, b);
  return {
    /** v10: the signed-in account's id (JWT sub) — the upload engine keeps each account's saved uploads apart. */
    whoAmI: async (): Promise<string | null> => {
      const tok = await getToken(); if (!tok) return null;
      try { const p = tok.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"); return JSON.parse(atob(p.padEnd(Math.ceil(p.length / 4) * 4, "="))).sub ?? null; }
      catch { return null; }
    },
    summary: () => c<Summary>("GET", "/v1/me/titulos"),
    myObras: (oficio: string) => c<{ obras: { obra_id: string; work_date: string | null; is_obra_maestra: boolean; thumb: string | null }[] }>("GET", `/v1/me/obras?oficio=${encodeURIComponent(oficio)}`),
    uploads: (count: number, content_type = "image/jpeg") => c<{ uploads: { path: string; signed_url: string; token: string }[] }>("POST", "/v1/me/uploads", { count, content_type }),
    partners: () => c<{ partners: { partner_id: string; name: string; portfolio_display: boolean; portfolio_display_at: string | null }[] }>("GET", "/v1/me/partners"),
    setPortfolioDisplay: (partnerId: string, on: boolean) => c<{ ok: boolean; portfolio_display: boolean; removed: number }>("PUT", `/v1/me/partners/${partnerId}/portfolio-display`, { on }),
    importGroups: (groups: unknown[]) => c<{ accepted: number; skipped_small: number }>("POST", "/v1/me/obras/import", { groups }),
    answerTag: (obraId: string, yes: boolean) => c("POST", `/v1/me/tags/${obraId}/${yes ? "confirm" : "decline"}`, {}),
    setVisibility: (oficio: string, visibility: string, opts: { share_phones?: string[]; display_name?: string } = {}) =>
      c("PUT", `/v1/me/titulos/${oficio}/visibility`, { visibility, ...opts }),
    obraMaestra: (obraId: string) => c("POST", `/v1/me/obras/${obraId}/obra-maestra`, {}),
    vistoBueno: (oficio: string) => c("POST", "/v1/me/visto-bueno", { oficio_key: oficio, ok_to_call: true }),
    appeal: (obraId: string) => c("POST", `/v1/me/obras/${obraId}/appeal`, {}),
    access: () => c<{ study_full: boolean; until: string | null; maestro_code: string | null }>("GET", "/v1/me/access"),
    redeemCode: (code: string) => c<{ ok: boolean }>("POST", "/v1/me/access/code", { code }),
    // The Carta PDF comes from its own function next to mdo-api (same sign-in).
    carta: (oficio: string, withPhoto = false) =>
      call<{ url: string; carta_number: string }>(apiUrl.replace(/mdo-api\/?$/, "mdo-carta-generate"), getToken, "GET",
        `?oficio=${encodeURIComponent(oficio)}${withPhoto ? "&photo=1" : ""}`),
  };
}
export type MdoClient = ReturnType<typeof mdoClient>;

// ---------------------------------------------------------------- owner console (through GM's ops-mdo)
export type Tab = "obras" | "visto_bueno" | "apelaciones" | "senales" | "fraude";
export function opsClient(opsUrl: string, getToken: TokenGetter) {
  const c = <T>(m: string, p: string, b?: unknown) => call<T>(opsUrl, getToken, m, p, b);
  return {
    counts: () => c<Record<string, number>>("GET", "/counts"),
    queue: (tab: Tab) => c<{ tab: Tab; items: Record<string, any>[] }>("GET", `/queue?tab=${tab}`),
    decideObra: (id: string, outcome: "aprobada" | "todavia_no", fix_note?: string) => c("POST", `/obras/${id}/decide`, { outcome, fix_note }),
    batchApprove: (ids: string[]) => c<{ approved: number }>("POST", "/obras/batch-approve", { obra_ids: ids }),
    decideReview: (id: string, outcome: "aprobada" | "todavia_no", marks?: Record<string, string>, fix_note?: string) =>
      c("POST", `/reviews/${id}/decide`, { outcome, marks, fix_note }),
    resolveFlag: (obraId: string, action: "dismiss" | "escalate", reason: string) => c("POST", `/flags/${obraId}/resolve`, { action, reason }),
    revokePropose: (obraId: string, reason: string) => c<{ proposal_id: string }>("POST", "/titulos/revoke/propose", { obra_id: obraId, reason }),
    revokeConfirm: (proposalId: string) => c("POST", "/titulos/revoke/confirm", { proposal_id: proposalId }),
    foundingGrant: (personId: string, oficio: string) => c("POST", "/titulos/founding_grant", { person_id: personId, oficio_key: oficio }),
    // MDO-10 calibration (blind)
    calNext: (set: string) => c<{ progress: { graded: number; total: number }; obra: Record<string, any> | null }>("GET", `/calibration/next?set=${encodeURIComponent(set)}`),
    calGrade: (set: string, obraId: string, outcome: "aprobada" | "todavia_no", fix_note?: string) =>
      c("POST", "/calibration/grade", { set, obra_id: obraId, outcome, fix_note }),
    calReport: (set: string) => c<Record<string, any>>("GET", `/calibration/report?set=${encodeURIComponent(set)}`),
    calRun: (set: string) => c<{ ran: number; failed: number }>("POST", "/calibration/run", { set }),
  };
}
export type OpsClient = ReturnType<typeof opsClient>;
